/**
 * Unified Analytics Service
 *
 * Provides a single interface for all analytics tracking,
 * combining Cloudflare Zaraz (for GA4) and Microsoft Clarity.
 */

import zarazService from './zaraz'

class AnalyticsService {
  constructor() {
    this.clarityEnabled = import.meta.env.VITE_CLARITY_ENABLED === 'true'
    this.debugMode = import.meta.env.DEV
    this.currentUserId = null
    this.anonymousId = null
    this.userLevel = 'anon' // Default to anonymous
  }

  /**
   * Set the current user ID for all analytics tracking
   * @param {string|null} userId - User identifier or null to clear
   */
  setUserId(userId) {
    this.currentUserId = userId

    // Set user ID in Zaraz for all future events
    if (userId) {
      zarazService.setUserProperty('user_id', userId)
    } else {
      zarazService.setUserProperty('user_id', null)
    }

    // Set user ID in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        if (userId) {
          window.clarity('identify', userId)
        }
      } catch (error) {
        console.error('Failed to set Clarity user ID:', error)
      }
    }
  }

  /**
   * Set the anonymous ID for tracking
   * @param {string} anonymousId - Anonymous identifier
   */
  setAnonymousId(anonymousId) {
    this.anonymousId = anonymousId

    // Set anonymous ID in Zaraz
    if (anonymousId) {
      zarazService.setUserProperty('anonymous_id', anonymousId)
    }

    // Set anonymous ID in Clarity as custom property
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('set', 'anonymous_id', anonymousId)
      } catch (error) {
        console.error('Failed to set Clarity anonymous ID:', error)
      }
    }
  }

  /**
   * Set the user level for tracking
   * @param {string} userLevel - User level (anon, guest, beta, admin, etc.)
   */
  setUserLevel(userLevel) {
    this.userLevel = userLevel || 'anon'

    // Set user level in Zaraz
    zarazService.setUserProperty('user_level', this.userLevel)

    // Set user level in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('set', 'user_level', this.userLevel)
      } catch (error) {
        console.error('Failed to set Clarity user level:', error)
      }
    }
  }

  /**
   * Track a custom event across all analytics platforms
   * @param {string} eventName - The name of the event
   * @param {Object} parameters - Event parameters
   */
  trackEvent(eventName, parameters = {}) {
    // Add user identifiers and level to parameters
    const enrichedParams = { ...parameters }

    // Always include user level
    enrichedParams.user_level = this.userLevel

    // Include appropriate identifier based on authentication status
    if (this.currentUserId) {
      enrichedParams.user_id = this.currentUserId
      // Also include anonymous ID for tracking conversion
      if (this.anonymousId) {
        enrichedParams.previous_anonymous_id = this.anonymousId
      }
    } else if (this.anonymousId) {
      enrichedParams.anonymous_id = this.anonymousId
    }

    // Track in Zaraz (GA4)
    zarazService.trackEvent(eventName, enrichedParams)

    // Track in Clarity if enabled
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('event', eventName)

        // For Clarity, we can also set custom tags for the event
        if (parameters.category) {
          window.clarity('set', 'event_category', parameters.category)
        }
      } catch (error) {
        console.error('Failed to track Clarity event:', error)
      }
    }
  }

  /**
   * Track page views (for SPA route changes)
   * @param {string} path - The page path
   * @param {string} title - The page title
   */
  trackPageView(path, title) {
    // Track in Zaraz
    zarazService.trackPageView(path, title)

    // Clarity automatically tracks page views
    // No additional action needed
  }

  /**
   * Track user authentication
   * @param {string} method - Authentication method
   * @param {string} userId - User identifier
   * @param {boolean} isNewUser - Whether this is a new user
   */
  trackAuth(method, userId, isNewUser = false) {
    // Set the user ID globally for all future events
    this.setUserId(userId)

    // Track in Zaraz
    zarazService.trackAuth(method, userId)

    // Track signup vs login
    if (isNewUser) {
      this.trackEvent('sign_up', { method })
    }

    // Set additional user properties in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('set', 'auth_method', method)
        window.clarity('event', isNewUser ? 'signup' : 'login')
      } catch (error) {
        console.error('Failed to set Clarity user properties:', error)
      }
    }
  }

  /**
   * Track user sign out
   */
  trackSignOut() {
    // Track in Zaraz
    zarazService.trackSignOut()

    // Track in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('event', 'logout')
      } catch (error) {
        console.error('Failed to track Clarity sign out:', error)
      }
    }

    // Clear the user ID globally but keep anonymous ID and reset to anon level
    this.setUserId(null)
    this.setUserLevel('anon')
    // Anonymous ID will be set by the calling code
  }

  /**
   * Track errors
   * @param {string} description - Error description
   * @param {boolean} fatal - Whether the error is fatal
   */
  trackError(description, fatal = false) {
    // Track in Zaraz
    zarazService.trackError(description, fatal)

    // Track in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('event', 'error')
        window.clarity('set', 'error_description', description)
        window.clarity('set', 'error_fatal', fatal.toString())
      } catch (error) {
        console.error('Failed to track Clarity error:', error)
      }
    }
  }

  /**
   * Track search/chat interactions
   * @param {string} query - Search query or chat message
   * @param {string} type - Type of interaction
   */
  trackSearch(query, type = 'search') {
    // Track in Zaraz
    zarazService.trackSearch(query, type)

    // Track in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('event', type === 'chat' ? 'chat_message' : 'search')
        // Don't send the actual query to Clarity for privacy
        window.clarity('set', 'interaction_type', type)
      } catch (error) {
        console.error('Failed to track Clarity search:', error)
      }
    }
  }

  /**
   * Track user engagement
   * @param {string} category - Engagement category
   * @param {string} action - Action taken
   * @param {string} label - Optional label
   * @param {number} value - Optional numeric value
   */
  trackEngagement(category, action, label, value) {
    // Track in Zaraz
    zarazService.trackEngagement(category, action, label, value)

    // Track in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('event', `${category}_${action}`)
        if (label) {
          window.clarity('set', 'engagement_label', label)
        }
      } catch (error) {
        console.error('Failed to track Clarity engagement:', error)
      }
    }
  }

  /**
   * Track timing events (performance metrics)
   * @param {string} category - Timing category
   * @param {string} variable - Variable being timed
   * @param {number} value - Time in milliseconds
   * @param {string} label - Optional label
   */
  trackTiming(category, variable, value, label) {
    const parameters = {
      event_category: category,
      name: variable,
      value: Math.round(value),
    }

    if (label) parameters.event_label = label

    // Track in Zaraz as custom event
    zarazService.trackEvent('timing_complete', parameters)

    // Track in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('event', 'performance_timing')
        window.clarity('set', 'timing_category', category)
        window.clarity('set', 'timing_variable', variable)
        window.clarity('set', 'timing_value', value.toString())
      } catch (error) {
        console.error('Failed to track Clarity timing:', error)
      }
    }
  }

  /**
   * Set user properties across all platforms
   * @param {Object} properties - User properties to set
   */
  setUserProperties(properties) {
    // Extract and update user level if provided
    if (properties.user_level) {
      this.setUserLevel(properties.user_level)
    }

    // Set in Zaraz
    Object.entries(properties).forEach(([key, value]) => {
      zarazService.setUserProperty(key, value)
    })

    // Set in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        Object.entries(properties).forEach(([key, value]) => {
          window.clarity('set', key, value)
        })
      } catch (error) {
        console.error('Failed to set Clarity user properties:', error)
      }
    }
  }

  /**
   * Track conversion events
   * @param {string} conversionType - Type of conversion
   * @param {Object} data - Conversion data
   */
  trackConversion(conversionType, data = {}) {
    const eventName = `conversion_${conversionType}`

    // Track in Zaraz with conversion data
    zarazService.trackEvent(eventName, {
      ...data,
      conversion_type: conversionType,
    })

    // Track in Clarity
    if (this.clarityEnabled && window.clarity) {
      try {
        window.clarity('event', eventName)
        window.clarity('set', 'conversion_type', conversionType)
      } catch (error) {
        console.error('Failed to track Clarity conversion:', error)
      }
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService()

export default analyticsService
